import { Link } from "react-router-dom";
import { Users, FileText, Receipt, Briefcase, CalendarDays, PiggyBank, Bot, Globe, ArrowRight, Check, TrendingUp } from "lucide-react";
import { BrowserFrame, TabletFrame, PhoneFrame } from "@/marketing/DeviceFrames";

const HERO = "/mockups/hero.webp";
const SHOT_DASHBOARD = "/mockups/dashboard.png";
const SHOT_INVOICE = "/mockups/quotes.png";
const SHOT_MOBILE = "/mockups/reports-mobile.png";

const MODULES = [
  { icon: Users, title: "Customer Hub", desc: "One tidy place for every customer, with a full activity timeline." },
  { icon: TrendingUp, title: "Leads Pipeline", desc: "Drag deals across stages from first enquiry to won." },
  { icon: FileText, title: "Quotes & Estimates", desc: "Build VAT-aware quotes and send branded PDFs in seconds." },
  { icon: Receipt, title: "Invoices & Payments", desc: "Get paid faster with reminders and aged-debtor tracking." },
  { icon: Briefcase, title: "Jobs & Sign-off", desc: "Schedule work, capture reports, photos and signatures." },
  { icon: CalendarDays, title: "Planner", desc: "See your week at a glance across day, week and list views." },
  { icon: PiggyBank, title: "Expenses & VAT", desc: "Log costs, snap receipts and see clear VAT summaries." },
  { icon: Bot, title: "Alfie AI", desc: "Drafts quotes, reminders and summaries — you approve everything." },
];

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy grain-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-blue-200 text-xs font-semibold tracking-wide uppercase">All-in-one operations platform</span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">Run your business in <span className="text-blue-400">one place.</span></h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">CRM, leads, quotes, invoices, jobs, planner and expenses — plus Alfie AI to do the paperwork. Built for sole traders, tradespeople and small teams.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-start-free" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 font-semibold shadow-lg transition-colors">Start free <ArrowRight size={18} /></Link>
              <Link to="/pricing" data-testid="hero-see-pricing" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 font-semibold transition-colors">See pricing</Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-slate-400">
              <span className="flex items-center gap-2"><Check size={16} className="text-blue-400" /> Card required — no charge on Free</span>
              <span className="flex items-center gap-2"><Check size={16} className="text-blue-400" /> Free plan forever</span>
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <img src={HERO} alt="Amden One reports and analytics on a tablet at a job site" className="rounded-2xl shadow-2xl ring-1 ring-white/10 object-cover w-full h-[420px]" />
            <div className="absolute -bottom-5 -left-5 bg-white text-navy rounded-xl shadow-xl p-4 hidden sm:block">
              <p className="text-xs text-slate-500 font-semibold uppercase">Owed to you</p>
              <p className="text-2xl font-mono font-semibold">€12,480</p>
            </div>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-navy">Everything your business does, in one system</h2>
          <p className="mt-4 text-slate-600 text-lg">Stop juggling spreadsheets, notebooks and five different apps. Amden One keeps it all together in plain language.</p>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {MODULES.map((m, i) => (
            <div key={m.title} className="amden-card p-6 hover:border-slate-300 hover:shadow-md transition-all animate-fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-blue-50 text-blue-accent"><m.icon size={22} /></span>
              <h3 className="mt-4 font-semibold text-navy text-lg">{m.title}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Product showcase */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-blue-accent font-semibold text-sm uppercase tracking-wide">See it in action</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-navy">A real look at the platform</h2>
            <p className="mt-4 text-slate-600 text-lg">The same clean, plain-language screens on desktop and out on the job.</p>
          </div>

          <div className="mt-12 grid lg:grid-cols-2 gap-6 items-center">
            <BrowserFrame src={SHOT_DASHBOARD} alt="Amden One dashboard" className="animate-fade-up" />
            <div className="animate-fade-up" style={{ animationDelay: "0.1s" }}>
              <h3 className="text-2xl font-bold text-navy">Your whole business at a glance</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">Money in, who owes you, what's overdue and today's live jobs — no accounting jargon, just the numbers that matter, updated as you work.</p>
            </div>
          </div>

          <div className="mt-16 grid lg:grid-cols-2 gap-6 items-center">
            <div className="order-2 lg:order-1 animate-fade-up">
              <h3 className="text-2xl font-bold text-navy">Branded quotes & invoices in seconds</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">VAT-aware line items, professional PDFs with your branding, one-tap payment recording and automatic overdue tracking.</p>
            </div>
            <BrowserFrame src={SHOT_INVOICE} alt="Amden One quotes & estimates" className="order-1 lg:order-2 animate-fade-up" />
          </div>

          <div className="mt-16 grid lg:grid-cols-2 gap-10 items-center">
            <div className="flex justify-center items-end pt-6">
              <TabletFrame src={SHOT_DASHBOARD} alt="Amden One on a tablet" className="w-[80%] max-w-md animate-fade-up" />
              <PhoneFrame src={SHOT_MOBILE} alt="Amden One on a phone" className="w-[36%] max-w-[150px] -ml-14 -mb-4 relative z-10 animate-fade-up" />
            </div>
            <div className="animate-fade-up" style={{ animationDelay: "0.1s" }}>
              <h3 className="text-2xl font-bold text-navy">Works on the phone, tablet and desktop</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">The same clean Amden One workspace follows your team everywhere. Clock in, work assigned jobs, snap site photos, complete reports and capture the customer's signature — right from a phone or tablet.</p>
              <ul className="mt-5 space-y-3">
                {["Snap and attach site photos", "Capture on-site customer signatures", "Recurring jobs schedule themselves"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-slate-700"><span className="grid place-items-center h-6 w-6 rounded-full bg-emerald-100 text-emerald-600"><Check size={14} /></span>{t}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Alfie band */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-blue-accent font-semibold text-sm"><Bot size={18} /> Meet Alfie</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-navy">Your AI assistant that never sends without asking</h2>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">Alfie drafts quotes, chases unpaid invoices and summarises customers in seconds. Nothing is sent or saved until you tap approve — you stay in control.</p>
            <ul className="mt-6 space-y-3">
              {["Draft polite payment reminders", "Summarise a customer's whole history", "Write quote emails in your voice"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-slate-700"><span className="grid place-items-center h-6 w-6 rounded-full bg-emerald-100 text-emerald-600"><Check size={14} /></span>{t}</li>
              ))}
            </ul>
            <Link to="/alfie" className="mt-8 inline-flex items-center gap-2 text-blue-accent font-semibold hover:gap-3 transition-all">Learn about Alfie <ArrowRight size={18} /></Link>
          </div>
          <div className="amden-card p-6 shadow-lg">
            <div className="flex items-center gap-2 pb-4 border-b border-slate-100"><span className="grid place-items-center h-8 w-8 rounded-lg bg-navy text-white"><Bot size={16} /></span><span className="font-semibold text-navy">Alfie</span></div>
            <div className="py-4 space-y-3 text-sm">
              <p className="bg-slate-100 rounded-2xl rounded-tl-sm px-4 py-2 text-slate-700 max-w-[85%]">Draft a reminder for INV-1006, it's 16 days overdue.</p>
              <p className="bg-blue-50 rounded-2xl rounded-tr-sm px-4 py-2 text-slate-800 ml-auto max-w-[90%]">Here's a friendly nudge for Aoife Murphy about the €940.95 balance. Want me to send it?</p>
            </div>
            <div className="flex gap-2 pt-3 border-t border-slate-100">
              <button className="flex-1 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium">Approve & send</button>
              <button className="px-3 py-2 rounded-lg border border-slate-300 text-sm">Edit</button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="bg-navy grain-navy rounded-3xl px-8 py-14 text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Ready to get organised?</h2>
          <p className="mt-4 text-slate-300 text-lg max-w-xl mx-auto">Start on the free plan today. Upgrade when you're ready for teams, materials and Alfie AI.</p>
          <Link to="/register" data-testid="cta-start-free" className="mt-8 inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 font-semibold shadow-lg transition-colors">Create your free account <ArrowRight size={18} /></Link>
        </div>
      </section>
    </div>
  );
}
